import { createFavoriteButton } from '@liveauctioneers/hammer-ui-core/card/components/createFavoriteButton';
import { createImage } from '@liveauctioneers/hammer-ui-core/card/components/createImage';
import { createInfo } from '@liveauctioneers/hammer-ui-core/card/components/createInfo';
import { cardFactory } from '@liveauctioneers/hammer-ui-core/card/factory';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import ItemCardImage from '@/components/GridItemCard/Card/components/ItemCardImage/ItemCardImage';
import ItemCardPriceRow from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardShortDate from '@/components/GridItemCard/Card/components/ItemCardShortDate/ItemCardShortDate';
import { useRequireLogin } from '@/components/ShouldLogIn/hooks/useRequireLogin';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import { toggleItemSave } from '@/redux/modules/item/saved/savedItems.actions';
import { isItemSaved } from '@/redux/modules/item/saved/savedItems.selectors';
import { CompleteAccountModalContext, ModalContext } from '@/redux/modules/modal';
import { ItemSummary } from '@/types/item/ItemSummary';
import { getItemUrlFromSlug, getSellerUrl } from '@/utils/urls';

export const TrendingItemCard = cardFactory<ItemSummary>({
    components: [
        createFavoriteButton({
            useIsFavorited: ({ itemId }) => {
                const isFavorite = useAppSelector((state) => isItemSaved(state, itemId));
                return isFavorite;
            },
            useSetFavorited: ({ itemId }) => {
                const dispatch = useAppDispatch();
                const verifyLogin = useRequireLogin();
                const location = useLocation();
                const modalContext = useMemo<ModalContext>(
                    () => ({
                        eventContext: EventContext.saveItem,
                        id: itemId,
                        itemId,
                        type: CompleteAccountModalContext.SaveItem,
                    }),
                    [itemId]
                );

                const onToggleSave = useCallback(() => {
                    dispatch(toggleItemSave({ canHaveEasyShippingTag: false, itemId, location }));
                }, [dispatch, itemId, location]);

                return () => {
                    verifyLogin({
                        modalContext,
                        requireLogin: true,
                        runFunction: () => onToggleSave(),
                    });
                };
            },
        }),

        createImage<ItemSummary>({
            ImageComponent: ({ itemId }) => {
                return (
                    <ItemCardImage
                        className="h-[172px] w-[172px]"
                        itemId={itemId}
                    />
                );
            },
        }),
        { spacerPx: 8 },

        createInfo({
            className: 'line-clamp-0',
            useText: ({ itemId, slugWithLocation, title }) => {
                return (
                    <SubtleMonochromeLink
                        className="line-clamp-2"
                        href={getItemUrlFromSlug(itemId, slugWithLocation)}
                    >
                        {title}
                    </SubtleMonochromeLink>
                );
            },
        }),
        { spacerPx: 2 },
        createInfo({
            className: 'line-clamp-0',
            useText: ({ bidCount, itemId }) => {
                const [linkHover, setLinkHover] = useState(false);

                const enterLinkHoverState = useCallback(() => {
                    setLinkHover(true);
                }, []);

                const leaveLinkHoverState = useCallback(() => {
                    setLinkHover(false);
                }, []);

                return (
                    <ItemCardPriceRow
                        isHovered={linkHover}
                        itemId={itemId}
                        onMouseEnter={enterLinkHoverState}
                        onMouseLeave={leaveLinkHoverState}
                        showBidCount={Boolean(bidCount)}
                    />
                );
            },
        }),
        { spacerPx: 16 },
        createInfo({
            className: 'line-clamp-0',
            useText: ({ itemId }) => {
                return (
                    <ItemCardShortDate
                        allowBidding
                        itemId={itemId}
                    />
                );
            },
        }),
        { spacerPx: 2 },
        createInfo({
            className: 'line-clamp-0',
            useText: ({ sellerId, sellerName }) => {
                return (
                    <SubtleMonochromeLink
                        className="line-clamp-1 text-link-secondary"
                        href={getSellerUrl(sellerId, sellerName)}
                        size="sm"
                    >
                        {sellerName}
                    </SubtleMonochromeLink>
                );
            },
        }),
    ],
    name: 'TrendingItemCard',
});
